import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useGeocodeAddress, type GeocodeResult } from "src/api/external/useGeocodeAddress";
import {
    looksLikeGeographyCode,
    useGeographyByCode,
    useSearchGeographies,
    type Geography,
} from "src/api/sure-api/useSearchGeographies";
import { FoodsAsyncComboboxField } from "src/components/EntityForm";
import { transportCreateTextFieldSx } from "./TransportCreatePageShell";
import { useTransportAppearance } from "./transportAppearance";

export type GeocodeInputValue = {
    label: string;
    latitude: number | null;
    longitude: number | null;
    countryCode: string | null;
};

type GeographyAutocompleteProps = {
    label: string;
    value: string;
    selectedGeography: Geography | null | undefined;
    disabled?: boolean;
    onChange: (code: string | null) => void;
};

type GeocodeInputProps = {
    label: string;
    currentLabel: string;
    latitude?: number | null;
    longitude?: number | null;
    disabled?: boolean;
    onPick: (picked: GeocodeInputValue | null) => void;
};

const getGeographyLabel = (geography: Geography) => `${geography.code} - ${geography.name}`;

const mergeGeographyOptions = (...groups: (Geography | null | undefined)[][]): Geography[] => {
    const seen = new Set<string>();
    const options: Geography[] = [];

    groups.flat().forEach((geography) => {
        if (!geography || seen.has(geography.code)) return;
        seen.add(geography.code);
        options.push(geography);
    });

    return options;
};

const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const handle = window.setTimeout(() => setDebounced(value), delayMs);

        return () => window.clearTimeout(handle);
    }, [value, delayMs]);

    return debounced;
};

export const TransportLegGeographyAutocomplete = ({
    label,
    value,
    selectedGeography,
    disabled,
    onChange,
}: GeographyAutocompleteProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const appearance = useTransportAppearance("product-v2");
    const [input, setInput] = useState("");
    const debouncedInput = useDebouncedValue(input.trim(), 350);
    const isCodeQuery = looksLikeGeographyCode(debouncedInput);
    const codeQuery = isCodeQuery ? debouncedInput.toUpperCase() : null;
    const { geography: codeGeography, isFetching: isFetchingCode } = useGeographyByCode(codeQuery, {
        enabled: !disabled,
    });
    const { geographies, isFetching: isFetchingNames } = useSearchGeographies({
        nameQuery: debouncedInput,
        enabled: !disabled && !isCodeQuery,
    });
    const options = useMemo(
        () => mergeGeographyOptions([selectedGeography], [codeGeography], geographies),
        [codeGeography, geographies, selectedGeography],
    );
    const selected = selectedGeography ?? (value !== "" ? value : null);

    if (appearance === "legacy") {
        return (
            <Autocomplete<Geography, false, false, true>
                freeSolo
                filterOptions={(items) => items}
                options={options}
                getOptionLabel={(option) => (typeof option === "string" ? option : getGeographyLabel(option))}
                isOptionEqualToValue={(option, optionValue) =>
                    typeof optionValue !== "string" && option.code === optionValue.code
                }
                value={selected}
                onInputChange={(_event, newInput, reason) => {
                    if (reason === "reset") return;
                    setInput(newInput);
                }}
                onChange={(_event, geography) => {
                    if (typeof geography === "string") {
                        onChange(geography.trim() === "" ? null : geography.trim());
                        return;
                    }

                    onChange(geography?.code ?? null);
                    setInput("");
                }}
                disabled={disabled}
                loading={isFetchingCode || isFetchingNames}
                fullWidth
                renderInput={(params) => <TextField {...params} label={label} sx={transportCreateTextFieldSx(theme)} />}
            />
        );
    }

    return (
        <FoodsAsyncComboboxField<Geography>
            label={label}
            options={options}
            getOptionId={(option) => option.code}
            getOptionLabel={getGeographyLabel}
            isOptionEqualToValue={(option, optionValue) => option.code === optionValue.code}
            value={selected}
            onInputChange={(newInput, reason) => {
                if (reason === "reset") return;
                setInput(newInput);
            }}
            onOptionChange={(geography) => {
                onChange(geography?.code ?? null);
                setInput("");
            }}
            onFreeTextCommit={(geography) => onChange(geography.trim() === "" ? null : geography.trim())}
            disabled={disabled}
            loading={isFetchingCode || isFetchingNames}
            loadingText={t("common.loading", "Loading...")}
            noOptionsText={t("component.select.noresultfound", "No results found")}
            clearLabel={t("common.clear", "Clear")}
            openLabel={t("component.select.open", "Open options")}
            closeLabel={t("common.close", "Close")}
        />
    );
};

export const TransportLegGeocodeInput = ({
    label,
    currentLabel,
    latitude,
    longitude,
    disabled,
    onPick,
}: GeocodeInputProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const appearance = useTransportAppearance("product-v2");
    const [input, setInput] = useState(currentLabel);

    useEffect(() => {
        setInput(currentLabel);
    }, [currentLabel]);

    const { data: results = [], isFetching } = useGeocodeAddress(input, { enabled: !disabled });

    if (appearance === "legacy") {
        const helperText =
            latitude != null && longitude != null ?
                `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
            :   t("transport.leg.address_hint", "Type an address, city, or port");

        return (
            <Autocomplete<GeocodeResult, false, false, true>
                freeSolo
                disabled={disabled}
                options={results}
                loading={isFetching}
                filterOptions={(options) => options}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
                inputValue={input}
                onInputChange={(_event, value) => {
                    setInput(value);
                    if (value === "") onPick(null);
                }}
                onChange={(_event, value) => {
                    if (value && typeof value !== "string") {
                        onPick(value);
                        setInput(value.label);
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        helperText={helperText}
                        placeholder={t("transport.leg.address_placeholder", "E.g. Marseille, France")}
                        sx={transportCreateTextFieldSx(theme)}
                    />
                )}
            />
        );
    }

    return (
        <FoodsAsyncComboboxField<GeocodeResult>
            label={label}
            value={null}
            inputValue={input}
            options={results}
            getOptionId={(option) => option.placeId}
            getOptionLabel={(option) => option.label}
            onInputChange={(nextInput) => {
                setInput(nextInput);
                if (nextInput === "") onPick(null);
            }}
            onOptionChange={(picked) => {
                if (picked === null) {
                    onPick(null);
                    return;
                }

                onPick(picked);
                setInput(picked.label);
            }}
            disabled={disabled}
            loading={isFetching}
            placeholder={t("transport.leg.address_hint", "Type an address, city, or port")}
            loadingText={t("common.loading", "Loading...")}
            noOptionsText={t("component.select.noresultfound", "No results found")}
            clearLabel={t("common.clear", "Clear")}
            openLabel={t("component.select.open", "Open options")}
            closeLabel={t("common.close", "Close")}
            showPopupIndicator={false}
        />
    );
};
